import React, { useState, useEffect } from "react";
import { Stack, TextInput, Textarea } from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { LookupDictionaryItem, LookupDictionaryItemInput } from "@/utils/api";
import { AppDrawer, StatusSelector } from "@/components/generic";
import dayjs from "dayjs";

export interface LookupDictionaryItemEditModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Lookup dictionary ID to associate the item with */
  lookupDictionaryId: string;

  /** Lookup dictionary item to edit (if null/undefined, it's create mode) */
  lookupDictionaryItem?: LookupDictionaryItem | null;

  /** Callback when item should be created */
  onCreateItem?: (
    data: LookupDictionaryItemInput
  ) => Promise<LookupDictionaryItem | null>;

  /** Callback when item should be updated */
  onUpdateItem?: (
    id: string,
    data: LookupDictionaryItemInput
  ) => Promise<LookupDictionaryItem | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface FormData {
  checkValue: string;
  filter1: string;
  filter2: string;
  expiresAt: Date | null;
  description: string;
  status: number;
}

const initialFormData: FormData = {
  checkValue: "",
  filter1: "",
  filter2: "",
  expiresAt: null,
  description: "",
  status: 100,
};

export const LookupDictionaryItemEditModal: React.FC<
  LookupDictionaryItemEditModalProps
> = ({
  opened,
  onClose,
  lookupDictionaryId,
  lookupDictionaryItem,
  onCreateItem,
  onUpdateItem,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | null | undefined>
  >({});

  const isEditMode = Boolean(lookupDictionaryItem?.id);
  const isSubmitting = isCreating || isUpdating;

  // Reset form when modal opens/closes or lookupDictionaryItem changes
  useEffect(() => {
    if (opened) {
      if (lookupDictionaryItem) {
        // Edit mode - populate form with data
        setFormData({
          checkValue: lookupDictionaryItem.checkValue || "",
          filter1: lookupDictionaryItem.filter1 || "",
          filter2: lookupDictionaryItem.filter2 || "",
          expiresAt: lookupDictionaryItem.expiresAt
            ? dayjs(lookupDictionaryItem.expiresAt).toDate()
            : null,
          description: lookupDictionaryItem.description || "",
          status: lookupDictionaryItem.status ?? 100,
        });
      } else {
        // Create mode - use defaults
        setFormData(initialFormData);
      }

      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, lookupDictionaryItem, onClearError]);

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Check value is required
    if (!formData.checkValue.trim()) {
      errors.checkValue = "Value is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle field changes
  const handleFieldChange = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Handle save
  const handleSubmit = async () => {
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      const submitData: LookupDictionaryItemInput = {
        lookupDictionaryId,
        checkValue: formData.checkValue.trim(),
        filter1: formData.filter1.trim() || null,
        filter2: formData.filter2.trim() || null,
        expiresAt: formData.expiresAt
          ? dayjs(formData.expiresAt).toISOString()
          : null,
        description: formData.description.trim() || null,
        status: formData.status,
      };

      let result: LookupDictionaryItem | null = null;

      if (isEditMode && lookupDictionaryItem?.id && onUpdateItem) {
        result = await onUpdateItem(lookupDictionaryItem.id, submitData);
      } else if (!isEditMode && onCreateItem) {
        result = await onCreateItem(submitData);
      }

      if (result) {
        onClose();
      }
    } catch (error) {
      console.error("Lookup dictionary item submission error:", error);
    }
  };

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={isEditMode ? "Edit Dictionary Item" : "Add Dictionary Item"}
      position="right"
      size="md"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={lastError}
      actionType="save-cancel"
      onConfirm={handleSubmit}
    >
      <Stack gap="md">
        <TextInput
          label="Value"
          placeholder="Enter the lookup value"
          value={formData.checkValue}
          onChange={(e) => handleFieldChange("checkValue", e.target.value)}
          error={validationErrors.checkValue}
          required
          withAsterisk
          size="md"
          description="The value to match during form validation"
        />

        <TextInput
          label="Filter 1"
          placeholder="Enter filter value (optional)"
          value={formData.filter1}
          onChange={(e) => handleFieldChange("filter1", e.target.value)}
          size="md"
          description="Optional filter for categorizing or narrowing lookups"
        />

        <TextInput
          label="Filter 2"
          placeholder="Enter filter value (optional)"
          value={formData.filter2}
          onChange={(e) => handleFieldChange("filter2", e.target.value)}
          size="md"
          description="Additional optional filter for more specific lookups"
        />

        <DateTimePicker
          label="Expires At"
          placeholder="Select expiration date and time"
          value={formData.expiresAt}
          onChange={(value) => handleFieldChange("expiresAt", value)}
          size="md"
          clearable
          description="Leave empty if the value never expires"
        />

        <Textarea
          label="Description"
          placeholder="Enter description (optional)"
          value={formData.description}
          onChange={(e) => handleFieldChange("description", e.target.value)}
          description="A note to help describe this dictionary item"
          minRows={2}
          maxRows={4}
          autosize
          size="md"
        />

        <StatusSelector
          label="Status"
          value={formData.status}
          onChange={(value) => handleFieldChange("status", value)}
          size="md"
        />
      </Stack>
    </AppDrawer>
  );
};
